import React from "react";
import { Box, Typography, Container, Grid } from "@mui/material";
import { motion } from "framer-motion";
import logo from "../../Assets/ABILIO REALTY LOGO 1.svg";
import FacebookIcon from "@mui/icons-material/Facebook";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const services = [
    "Residential Design",
    "Civil Infrastructure",
    "Project Management",
    "Property Resale",
  ];

  const company = [
    "Sustainability Report",
    "Investor Relations",
    "Career Opportunities",
    "News & Media",
  ];

  const legal = ["Privacy Policy", "Terms of Service", "Cookie Settings"];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const slideUpVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "auto",
        backgroundColor: "#FAFAF9",
        pt: "32px",
        pb: "32px",
        mx: "auto",
        borderTop: "1px solid #E7E5E4",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: "1216px",
          px: { xs: "16px", lg: "32px" },
        }}
      >
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
        >
          <Grid container spacing={8}>
            {/* Logo Box */}
            <Grid item xs={12} md={3}>
              <motion.div variants={slideUpVariants}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                    alignItems: { xs: "center", md: "flex-start" },
                  }}
                >
                  <Box
                    component="img"
                    src={logo}
                    alt="ABILIO REALTY"
                    sx={{
                      width: "120px",
                      height: "auto",
                      cursor: "pointer",
                      transition: "transform 0.3s ease",
                      "&:hover": {
                        transform: "scale(1.05)",
                      },
                    }}
                    onClick={scrollToTop}
                  />
                  <Typography
                    sx={{
                      fontFamily: "Inter",
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "22px",
                      color: "#78716C",
                      maxWidth: "268px",
                      textAlign: "left",
                    }}
                  >
                    Mastering the art of luxury interiors and the science of
                    modern infrastructure since 2008.
                  </Typography>
                  {/* Social Icons */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: "12px",
                      mt: "8px",
                      justifyContent: { xs: "center", md: "flex-start" },
                    }}
                  >
                    {[
                      {
                        name: "Facebook",
                        icon: <FacebookIcon sx={{ fontSize: 18 }} />,
                        color: "#1877F2",
                        url: "https://www.facebook.com/profile.php?id=61572110947556",
                      },
                      {
                        name: "LinkedIn",
                        icon: <LinkedInIcon sx={{ fontSize: 18 }} />,
                        color: "#0A66C2",
                        url: "https://www.linkedin.com/in/abilio-realty-6a3215400/",
                      },
                      {
                        name: "Instagram",
                        icon: <InstagramIcon sx={{ fontSize: 18 }} />,
                        color: "#E1306C",
                        url: "https://www.instagram.com/abiliorealty/?hl=en",
                      },
                      {
                        name: "YouTube",
                        icon: <YouTubeIcon sx={{ fontSize: 18 }} />,
                        color: "#FF0000",
                        url: "https://www.youtube.com/@AbilioRealty",
                      },
                    ].map((social) => (
                      <Box
                        key={social.name}
                        component="a"
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "50%",
                          backgroundColor: "#EBE7E7",
                          color: "#78716C",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          textDecoration: "none",
                          "&:hover": {
                            backgroundColor: social.color,
                            color: "#FFFFFF",
                            transform: "translateY(-4px)",
                            boxShadow: "0px 8px 16px rgba(0,0,0,0.15)",
                          },
                        }}
                      >
                        {social.icon}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </motion.div>
            </Grid>

            {/* Services Box */}
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={slideUpVariants}>
                <Box
                  sx={{
                    width: { xs: "100%", md: "268px" },
                    height: "168.25px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                    pb: "4.25px",
                    alignItems: { xs: "center", md: "flex-start" },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Inter",
                      fontWeight: 600,
                      fontSize: "16px",
                      lineHeight: "20px",
                      color: "#1C1917",
                      width: { xs: "auto", md: "268px" },
                      height: "20px",
                      textAlign: { xs: "center", md: "left" },
                    }}
                  >
                    Services
                  </Typography>
                  {services.map((item, index) => (
                    <Typography
                      key={item}
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "20px",
                        color: "#78716C",
                        width: { xs: "auto", md: "268px" },
                        height: "20px",
                        textAlign: { xs: "center", md: "left" },
                        cursor: "pointer",
                        transition: "color 0.3s ease, transform 0.2s ease",
                        "&:hover": {
                          color: "#B02F00",
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Box>
              </motion.div>
            </Grid>

            {/* Company Box */}
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={slideUpVariants}>
                <Box
                  sx={{
                    width: { xs: "100%", md: "268px" },
                    height: "168.25px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                    pb: "4.25px",
                    alignItems: { xs: "center", md: "flex-start" },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Inter",
                      fontWeight: 600,
                      fontSize: "16px",
                      lineHeight: "20px",
                      color: "#1C1917",
                      width: { xs: "auto", md: "268px" },
                      height: "20px",
                      textAlign: { xs: "center", md: "left" },
                    }}
                  >
                    Company
                  </Typography>
                  {company.map((item) => (
                    <Typography
                      key={item}
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "20px",
                        color: "#78716C",
                        width: { xs: "auto", md: "268px" },
                        height: "20px",
                        textAlign: { xs: "center", md: "left" },
                        cursor: "pointer",
                        transition: "color 0.3s ease, transform 0.2s ease",
                        "&:hover": {
                          color: "#B02F00",
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Box>
              </motion.div>
            </Grid>

            {/* Legal Box */}
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={slideUpVariants}>
                <Box
                  sx={{
                    width: { xs: "100%", md: "268px" },
                    height: "168.25px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                    pb: "4.25px",
                    alignItems: { xs: "center", md: "flex-start" },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Inter",
                      fontWeight: 600,
                      fontSize: "16px",
                      lineHeight: "20px",
                      color: "#1C1917",
                      width: { xs: "auto", md: "268px" },
                      height: "20px",
                      textAlign: { xs: "center", md: "left" },
                    }}
                  >
                    Legal
                  </Typography>
                  {legal.map((item) => (
                    <Typography
                      key={item}
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "20px",
                        color: "#78716C",
                        width: { xs: "auto", md: "268px" },
                        height: "20px",
                        textAlign: { xs: "center", md: "left" },
                        cursor: "pointer",
                        transition: "color 0.3s ease, transform 0.2s ease",
                        "&:hover": {
                          color: "#B02F00",
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>

        {/* Copyright Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "1216px",
              height: "30px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              pt: "31px",
              pb: "32px",
              borderTop: "1px solid #E7E5E4",
              mx: "auto",
              mt: "32px",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "100%",
                color: "#78716C",
                textAlign: "center",
              }}
            >
              © 2018 Abilio Realty. All rights reserved. Structural Elegance by
              Abilio.
            </Typography>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Footer;
